/**
 * The two capitalization rules of design-language#copy-voice, as predicates.
 *
 * Which rule a string falls under is decided by the control that renders it
 * ([ADR 0022](../../../docs/decisions/0022-the-control-decides-whether-a-string-is-a-label.md)),
 * [ADR 0041](../../../docs/decisions/0041-tab-labels-and-widget-captions-follow-one-casing-rule.md)
 * extends that to the widget, and
 * [ADR 0044](../../../docs/decisions/0044-button-captions-are-title-case.md)
 * puts a button caption on the Title Case side in both trees. Deciding that is each guard's job, because each
 * tree renders its controls differently. Checking the string once decided is
 * the same job everywhere, which is why the predicates live here rather than
 * as a copy in the portal's guard and another in the widget's: two copies of a
 * rule are how one surface ends up stricter than the other without anyone
 * choosing it. Nothing at runtime imports this module, so a bundler drops it.
 */

/** Lower-case unless they lead, per design-language#copy-voice's "small connectives stay lower". */
export const TITLE_CASE_SMALL_WORDS: ReadonlySet<string> = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'nor', 'of', 'on',
  'or', 'the', 'to', 'up', 'via', 'vs', 'with', 'per',
]);

/**
 * Proper nouns, which are spelled one way wherever they appear.
 *
 * This is VOCABULARY, not an exemption list: it names words that keep their
 * own spelling in sentence copy and in Title Case alike, and it never names a
 * caption or a file. A word belongs here only if it is a name (a product, a
 * service, a company). ADR 0044 records the same words (carried forward from
 * ADR 0039), and the portal guard reads the ADR and fails if the two drift. An
 * all-capitals acronym ("ID", "URL") needs no entry.
 */
export const PROPER_NOUNS: ReadonlySet<string> = new Set([
  'Azure', 'Brutus', 'DevStride', 'Entra', 'GitHub', 'Graph', 'Microsoft', 'Tenger',
]);

/** Stands in for an expression whose text a guard cannot know. */
export const CASING_OPAQUE = '\u0000';

/**
 * Title Case: every word leads with a capital except a small connective that
 * does not lead, which may stay lower ("File a Request", "Sign In with Tenger
 * ID"). A proper noun passes as spelled in `PROPER_NOUNS`, so a name whose
 * spelling leads lower-case is listed there rather than exempted. A parenthesised qualifier is not part of the label:
 * "Description Template (optional)" is correct, because the qualifier is
 * sentence copy attached to a label and Title-Casing it would read as a second
 * label.
 */
export function isTitleCase(label: string): boolean {
  const words = label.split(/\s+/).filter(Boolean);
  return words.every((word, index) => {
    if (word.startsWith('(') || word.includes(CASING_OPAQUE)) return true;
    const core = word.replace(/^[^\w]+/, '').replace(/[^\w]+$/, '');
    const first = core[0];
    // A word that is punctuation or a bare number carries no case to check.
    if (first === undefined || !/[a-zA-Z]/.test(first)) return true;
    if (index > 0 && TITLE_CASE_SMALL_WORDS.has(core.toLowerCase())) return true;
    if (PROPER_NOUNS.has(core)) return true;
    return first === first.toUpperCase();
  });
}

/**
 * Sentence case: the first word leads with a capital, and no later word does
 * unless it is a proper noun or an acronym. A word after an opaque expression
 * is not held to the first-word rule, because it may be mid-sentence
 * ("+{remaining} more").
 */
export function isSentenceCase(caption: string): boolean {
  let first = true;
  for (const word of caption.split(/\s+/).filter(Boolean)) {
    if (word.includes(CASING_OPAQUE)) {
      first = false;
      continue;
    }
    const core = word.replace(/^[^A-Za-z0-9]+/, '').replace(/[^A-Za-z0-9]+$/, '');
    const lead = core[0];
    if (lead === undefined || !/[A-Za-z]/.test(lead)) continue;
    if (first) {
      first = false;
      if (lead !== lead.toUpperCase()) return false;
      continue;
    }
    if (lead === lead.toLowerCase()) continue;
    if (PROPER_NOUNS.has(core) || /^[A-Z0-9]{2,}$/.test(core)) continue;
    return false;
  }
  return true;
}
